/**
 * Streaming tuning (plan R7, spec §4.4): `src/data/reach-streaming.json`, Zod-validated here. Every value is a
 * starting value for playtest; R12 exposes the radii and per-tick budgets in its "Session" tuning group.
 */
import { z } from 'zod'
import streamingJson from '../data/reach-streaming.json'

const positive = z.number().positive()
const nonNegative = z.number().min(0)
const count = z.number().int().min(0)

/** An inclusive [min, max] pair with min ≤ max. */
function minMax(item: z.ZodNumber): z.ZodType<[number, number]> {
  return z.tuple([item, item]).refine(([a, b]) => a <= b, { message: 'min must not exceed max' })
}

const perKind = <T extends z.ZodTypeAny>(item: T): z.ZodObject<{ asteroid: T; ice: T; gas: T }> =>
  z.object({ asteroid: item, ice: item, gas: item })

/** Weights over the six M1 archetypes A–F; at least one must be positive. */
const archetypeWeights = z
  .array(nonNegative)
  .length(6)
  .refine((w) => w.some((v) => v > 0), {
    message: 'at least one archetype weight must be positive',
  })

export const streamingConfigSchema = z
  .object({
    comment: z.string().optional(),
    cellSizeM: positive,
    spawnRadiusM: positive,
    despawnRadiusM: positive,
    checkEveryTicks: z.number().int().positive(),
    perTick: z.object({ clusters: count, cells: count, structures: count }),
    clusters: z.object({
      count: perKind(minMax(count)),
      placementFraction: z.number().min(0).max(1),
      minSeparationM: nonNegative,
      maxTries: z.number().int().positive(),
      cacheSize: count,
    }),
    background: z.object({
      rocksPerCell: perKind(nonNegative),
      radiusM: minMax(positive),
      radiusPower: positive,
      lowMinRadiusM: nonNegative,
      maxAttempts: z.number().int().positive(),
      homeClearM: nonNegative,
    }),
    archetypeMix: z.object({
      asteroid: archetypeWeights,
      ice: archetypeWeights,
    }),
    transientDespawnM: positive,
  })
  .refine((c) => c.despawnRadiusM > c.spawnRadiusM, {
    message: 'despawnRadiusM must exceed spawnRadiusM (hysteresis)',
  })

export type StreamingConfig = z.infer<typeof streamingConfigSchema>

/** Parses and validates reach-streaming.json (throws a ZodError naming the bad field). */
export function loadStreamingConfig(raw: unknown = streamingJson): StreamingConfig {
  return streamingConfigSchema.parse(raw)
}
